import { Injectable } from '@angular/core';
import { FilterInterface } from '../interfaces/filter.interface';


/**
 * Usage:
 *
 * // Register a filter
 * hookSystemService.addFilter('action-name', (data) => { data += 1; return data; }, 'filter-name', 10);
 *
 * // Example input
 * let number = 1;
 *
 * // Trigger filters based on an action
 * number = hookSystemService.filter('action-name', number);
 *
 * // Example result of filtered data
 * console.log('Number: ', number); // Result => Number: 2
 */
@Injectable()
export class HookSystemService {
    /**
     * A collection of actions and their corresponding filters.
     *
     * @type Array<FilterInterface>
     */
    private filters = Array<FilterInterface>();

    /**
     * A collection of events and their corresponding filters.
     *
     * @type Array<FilterInterface>
     */
    private events = Array<FilterInterface>();

    /**
     * Constructor
     */
    constructor() {
        //
    }

    public getFilter(name) {
        return this.filters.find(filter => filter.name === name);
    }

    /**
     * Register a hook
     *
     * @param {string} action
     * @param {any} callback
     * @param {string} name
     * @param {number} order
     * @return void
     */
    public addFilter(action: string, callback: any, name: string = '', order: number = 999) {
        this.filters.push({
            action: action,
            callback: callback,
            name: name,
            order: order
        });

        this.filters.sort(this.dynamicSortMultiple('action', 'order'));
    }

    /**
     * Add filter alias.
     *
     * @param action
     * @param callback
     * @param name
     */
    public on<T>(action: string, callback: (data: T) => T, name?: string) {
        return this.addFilter(action, callback, name);
    }

    /**
     * Filter data
     *
     * @param action
     * @param data
     */
    public filter(action: string, data: any) {
        const filtersToProcess = this.filters.filter(filter => filter.action === action);

        filtersToProcess.forEach(filter => {
            data = filter.callback(data);
        });

        return data;
    }

    /**
     * Filter alias.
     *
     * @param action
     * @param data
     */
    public fire<T>(action: string, data: T): T {
        return this.filter(action, data);
    }

    /**
     * Remove a filter.
     *
     * @param {string} name
     * @param {string} action
     */
    public removeFilter(name: string, action: string = null) {
        if (action !== null) {
            this.filters = this.filters.filter(filter => filter.name !== name || filter.action !== action);
        } else {
            this.filters = this.filters.filter(filter => filter.name !== name);
        }
    }

    /**
     * Add an event handler
     *
     * @param eventName
     * @param callback
     * @param name
     * @param order
     */
    public addEventHandler(eventName: string, callback: any, name: string = '', order: number = 999) {
        this.events.push({
            action: eventName,
            callback: callback,
            name: name,
            order: order
        });

        this.events.sort(this.dynamicSortMultiple('action', 'order'));
    }

    /**
     * Trigger an event
     *
     * @param {string} action
     * @param {any} data
     * @return {any}
     */
    public event(eventName: string, data: any) {
        const eventsToProcess = this.events.filter(event => event.action === eventName);

        eventsToProcess.forEach(event => {
            data = event.callback(data);
        });

        return data;
    }

    /**
     * Remove an event.
     *
     * @param {string} name
     * @param {string} eventName
     */
    public removeEventHandler(name: string, eventName: string = null) {
        if (eventName !== null) {
            this.filters = this.filters.filter(event => event.name !== name || event.action !== eventName);
        } else {
            this.filters = this.filters.filter(event => event.name !== name);
        }
    }

    /**
     * Sort an array of objects based on the given property
     *
     * Example:
     * var people = [
     *      {name: "John", surname: "Doe"},
     *      {name:"Kaiser", surname:"Software"}
     * ];
     *
     * people.sort(dynamicSort('name')); // Sort ascending
     * people.sort(dynamicSort('-name')); // Sort descending
     *
     * @param property
     * @return {(a, b) => number}
     */
    private dynamicSort(property) {
        let sortOrder = 1;
        if (property[0] === "-") {
            sortOrder = -1;
            property  = property.substr(1);
        }
        return (a, b) => {
            const result = (a[property] < b[property]) ? -1 : (a[property] > b[property]) ? 1 : 0;
            return result * sortOrder;
        }
    }

    /**
     * Sort an array of objects based on the given properties
     *
     * Example:
     * var people = [
     *      {name: "John", surname: "Doe"},
     *      {name:"Kaiser", surname:"Software"}
     * ];
     *
     * people.sort(dynamicSortMultiple('name', 'surname)); // Sort ascending, ascending
     * people.sort(dynamicSortMultiple('name', '-surname')); // Sort ascending, descending
     * @return {(obj1, obj2) => number}
     */
    private dynamicSortMultiple(...args: any[]) {
        const numberOfProperties = args.length;
        return (obj1, obj2) => {
            let i      = 0;
            let result = 0;
            /* try getting a different result from 0 (equal)
             * as long as we have extra properties to compare
             */
            while (result === 0 && i < numberOfProperties) {
                result = this.dynamicSort(args[i])(obj1, obj2);
                i++;
            }
            return result;
        }
    }
}


// WEBPACK FOOTER //
// ./libs/hook-system/src/app/services/hook-system.service.ts